import React, { useEffect, useRef, useState } from 'react';
import { uploadFile } from '../../../helpers/fileHelpers/imageUploadHelper';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { TENANT_KEY } from '../../../utility/env';
import tenantConstants from '@constants';
import { Spinner } from '..';
import { Button, Checkbox, ConfirmationModal, Icon, Popover, Skeleton } from '../';
import { strings } from '../../../constants/strings';
import { ImageEnlarge, ImageSelector } from '../../post-listing/image-select/styled';
import { IconEnlargeImage } from '../../svg';
import { Tag } from '../tag/tag';
import { CoverChip, DocumentsThumb, DragHandle, GalleryThumb, Remove, Retry, Thumbnail } from './styled';
import { v2Color } from '@theme/v2';

/* 6-dot drag affordance (Figma) — white dots with a soft drop shadow. */
const DragDotsSVG = () => (
  <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#rega_drag_dots)">
      <path d="M14.5003 12.0004C14.5003 10.6196 13.3809 9.50022 12.0001 9.50022C10.6193 9.50022 9.5 10.6196 9.5 12.0004C9.5 13.3811 10.6193 14.5005 12.0001 14.5005C13.3809 14.5005 14.5003 13.3811 14.5003 12.0004Z" fill="white" />
      <path d="M8.00027 12.0004C8.00027 10.6196 6.88092 9.50022 5.50014 9.50022C4.11935 9.50022 3 10.6196 3 12.0004C3 13.3811 4.11935 14.5005 5.50014 14.5005C6.88092 14.5005 8.00027 13.3811 8.00027 12.0004Z" fill="white" />
      <path d="M21.0003 12.0004C21.0003 10.6196 19.8809 9.50022 18.5001 9.50022C17.1193 9.50022 16 10.6196 16 12.0004C16 13.3811 17.1193 14.5005 18.5001 14.5005C19.8809 14.5005 21.0003 13.3811 21.0003 12.0004Z" fill="white" />
      <path d="M14.5003 5.50035C14.5003 4.11957 13.3809 3.00022 12.0001 3.00022C10.6193 3.00022 9.5 4.11957 9.5 5.50035C9.5 6.88114 10.6193 8.00049 12.0001 8.00049C13.3809 8.00049 14.5003 6.88114 14.5003 5.50035Z" fill="white" />
      <path d="M8.00027 5.50035C8.00027 4.11957 6.88092 3.00022 5.50014 3.00022C4.11935 3.00022 3 4.11957 3 5.50035C3 6.88114 4.11935 8.00049 5.50014 8.00049C6.88092 8.00049 8.00027 6.88114 8.00027 5.50035Z" fill="white" />
      <path d="M21.0003 5.50035C21.0003 4.11957 19.8809 3.00022 18.5001 3.00022C17.1193 3.00022 16 4.11957 16 5.50035C16 6.88114 17.1193 8.00049 18.5001 8.00049C19.8809 8.00049 21.0003 6.88114 21.0003 5.50035Z" fill="white" />
    </g>
    <defs>
      <filter id="rega_drag_dots" x="0" y="0" width="24.0005" height="17.5005" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
        <feOffset />
        <feGaussianBlur stdDeviation="1.5" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
      </filter>
    </defs>
  </svg>
);

const ImageUploadItem = (props) => {
  const { t } = useTranslation();
  const {
    uploadImage,
    item,
    removeImage,
    previewKey = 'gallerythumb',
    selectOnClick,
    selectMainOnDoubleClick,
    attachmentType,
    associationKey,
    files,
    setFiles,
    makeBlob,
    isSvg,
    size,
    getS3PreSignedUrl,
    imgContain,
    onRetry,
    fullPreviewKey,
    showClose = true,
    viewType,
    onImageUpload = () => {},
    imageAnalyser,
    borderRadius,
    showDuplicateWarning = true,
    redesign = false,
    uploadsOwnedByParent = false,
  } = props;
  const { uploaded, uploading, inError, selected, isMainImage, status, is_rega_image, analysing, analyseError, is_unique, image_type } = item;

  // Redesign (drag handle, Cover chip, repositioned duplicate warning) is opt-in via the
  // `redesign` prop so it only applies to the post-listing image field — not every uploader
  // in the tenant (e.g. the User Settings profile picture). Still gated on the tenant FORM flag.
  const isRedesign = redesign === true && !!tenantConstants?.FORM;

  // Post-listing (new upload): freshly added images have no `isMainImage` until a reorder,
  // so the Cover chip wouldn't show. Fall back to the first visible image being the cover —
  // redesign only, so non-post-listing uploaders (e.g. profile picture) keep the original
  // behaviour of showing "Cover" solely for an explicitly-main image.
  const anyMainImage = (files || []).some((f) => f?.isMainImage && !f?._destroy);
  const firstVisibleImageId = (files || []).find((f) => !f?._destroy)?.id;
  const showCoverChip =
    isMainImage || (isRedesign && !anyMainImage && item?.id != null && item.id === firstVisibleImageId);

  const [imageLoaded, setImageLoaded] = useState(false);
  const modalRef = useRef();
  // Starting the upload from a mount effect ties uploading to being rendered, which breaks the
  // moment a parent shows only some of the list (the gallery's "+N" collapse). A parent that owns
  // the whole list can take it over by passing `uploadsOwnedByParent`; the other callers of this
  // component, which render every item they have, keep the original behaviour.
  //
  // The list is passed as a ref so a late-finishing upload writes back what is on screen now, not
  // the snapshot taken when this item mounted.
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => {
    if (uploadsOwnedByParent) return;
    uploadFile(
      item,
      getS3PreSignedUrl,
      attachmentType,
      associationKey,
      filesRef,
      setFiles,
      makeBlob,
      isSvg,
      undefined,
      onImageUpload,
      imageAnalyser,
    );
  }, []);

  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  let timer = 0;
  let delay = 200;
  let prevent = false;

  const onImageClick = () => {
    // !selectOnClick && modalRef.current.showModal();
  };
  const onImageDoubleClick = () => {
    if (selectMainOnDoubleClick) {
      const updatedFiles = files.map((e) => ({
        ...e,
        isMainImage: e.id === item.id,
      }));
      setFiles(updatedFiles);
    }
  };

  const onClick = () => {
    timer = setTimeout(() => {
      !prevent && onImageClick();
      prevent = false;
    }, delay);
  };

  const onDoubleClick = () => {
    clearTimeout(timer);
    prevent = true;
    onImageDoubleClick();
  };

  const renderImage = (status) => {
    return (
      <Thumbnail
        style={{
          '--thumbnail-width': '100%',
          ...(status === 'Rejected' || status === 'rejected' ? { borderColor: 'red', borderStyle: 'solid' } : {}),
          // borderColor: 'red',
          // borderStyle: 'solid',
          '--border-radius': borderRadius,
        }}
        draggable={false}
        onClick={onClick}
        onDoubleClick={onImageDoubleClick}
        src={item[previewKey] ? item[previewKey] : item.localPreview}
      />
    );
  };

  const renderListView = () => {
    return (
      <DocumentsThumb className="gallery-thumb" imgContain={imgContain} faded={inError || uploading}>
        <div className="file-tile">
          <div style={{ wordBreak: 'break-all' }}>
            <Icon className="file-icon" icon="MdFileCopy" />
            {item?.name || item?.file?.name}
          </div>
          {inError && (
            <Retry
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onRetry(item);
              }}
            >
              {isMobile ? <Icon icon="MdRefresh" size={16} /> : 'Retry'}
            </Retry>
          )}
          {!uploading && showClose && (
            <Button
              icon="HiOutlineTrash"
              className="btnTrash"
              onClick={(e) => {
                e.preventDefault();
                removeImage(item);
              }}
            >
              {isMobile ? '' : 'Remove'}
            </Button>
          )}
          {!!uploading && <Spinner />}
        </div>
      </DocumentsThumb>
    );
  };

  const renderGalleryView = () => {
    return (
      <GalleryThumb
        className={`gallery-thumb${isRedesign ? ' redesign-thumb' : ''}${isRedesign && isMobile ? ' redesign-thumb-mobile' : ''}`}
        imgContain={imgContain}
        faded={inError || uploading}
      >
        {isRedesign && !uploading && !inError && (
          <DragHandle aria-hidden="true">
            <DragDotsSVG />
          </DragHandle>
        )}
        {selectOnClick && (
          <ImageSelector>
            <Checkbox
              value={selected}
              onChange={(event) => {
                setFiles((prevState) =>
                  prevState.map((e) => (e.id === item.id ? { ...e, selected: event.target.checked } : e)),
                );
              }}
              style={{ width: 96, height: 96 }}
            >
              {renderImage(status)}
            </Checkbox>
          </ImageSelector>
        )}

        {!isRedesign && (
          <ImageEnlarge
            onClick={(e) => {
              if (!uploading && !inError) {
                e.stopPropagation();
                setImageLoaded(true);
                modalRef?.current && modalRef.current.showModal();
              }
            }}
          >
            <IconEnlargeImage />
          </ImageEnlarge>
        )}
        {showDuplicateWarning && is_unique === false && image_type === "listing_image" && (
          <Popover
            content={t("Duplicate image detected. This image is currently in use on another {{tenant}} listing. It may reduce your property's health score.", {
              tenant: TENANT_KEY === 'zameen' ? 'Zameen' : 'Bayut',
            })}
            action="hover"
            placement="top"
            getPopupContainer={() => document.body}
          >
            <div
              style={{
                position: 'absolute',
                // Redesign pins the drag handle top-inline-start, the remove (cross) top-inline-end,
                // and the Cover chip bottom-inline-start — so the duplicate-image warning goes in the
                // remaining bottom-inline-end corner: on the same bottom line as the Cover chip but the
                // opposite side, so nothing overlaps. inset-inline-* keeps this correct in en (LTR) and
                // ar (RTL). Other tenants keep the original top-inline-start placement.
                ...(isRedesign
                  ? { insetInlineEnd: '6px', insetBlockEnd: '6px' }
                  : { insetInlineStart: '4px', insetBlockStart: '4px' }),
                zIndex: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
              }}
            >
              <AiOutlineInfoCircle size={16} color={v2Color('#ADB4D2')} />
            </div>
          </Popover>
        )}
        {!selectOnClick && renderImage(status)}
        {is_rega_image && (
          <Tag shape="round" style={{ alignSelf: 'top', fontSize: 12 }}>
            {t('Rega Image')}
          </Tag>
        )}
        {showCoverChip &&
          (isRedesign ? (
            <CoverChip $mobile={isMobile}>{t('Cover')}</CoverChip>
          ) : (
            <Tag shape="round" style={{ alignSelf: 'center', fontSize: 12 }}>
              {t('Cover')}
            </Tag>
          ))}
        {status === 'Rejected' ||
          (status === 'rejected' && (
            <Tag color={'red'} shape="round" style={{ alignSelf: 'center', fontSize: 12 }}>
              {t('Rejected')}
            </Tag>
          ))}
        {inError && (
          <Retry
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRetry(item);
            }}
          >
            {t('Retry')}
          </Retry>
        )}
        {!uploading && showClose && !is_rega_image && !analysing && (
          <Remove
            size="small"
            icon="MdClose"
            shape="circle"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              removeImage(item);
            }}
            style={{ '--ant-control-height-sm': isMobile && isRedesign ? '20px' : '24px' }}
          />
        )}
        {!!uploading && <Spinner />}
        <ConfirmationModal
          title={strings.preview}
          ref={modalRef}
          footer={null}
          width={'auto'}
          bodyStyle={{
            minWidth: isMobile ? 150 : 300,
            minHeight: isMobile ? 'auto' : 300,
          }}
        >
          {imageLoaded ? (
            <Thumbnail
              style={{ '--thumbnail-width': 'auto', '--thumbnail-height': 'auto', maxWidth: '100%', maxHeight: '100%' }}
              src={
                item?.full
                  ? item?.full
                  : item?.gallerythumb
                    ? item?.gallerythumb.replace('thumbnail', 'full')
                    : item?.localPreview
              }
              onLoad={(e) => {
                setImageLoaded(true);
              }}
            />
          ) : (
            <div
              className="flex justify-content-center align-items-center"
              style={{ width: 600, height: 600, backgroundColor: 'aliceblue' }}
            >
              <Skeleton type="image" active={false} />
            </div>
          )}
        </ConfirmationModal>
      </GalleryThumb>
    );
  };

  return viewType === 'list' ? renderListView() : renderGalleryView();
};

export default ImageUploadItem;
